import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';

import { NotificationsService } from 'angular2-notifications';
import pLimit from 'p-limit';
import { v4 as uuid } from 'uuid';

import { ParentTypes, creatorSupportedTypes } from '@23degrees/shared-code/config/constants';
import { applyDataSetUpdateToExistingContent, getContentChildren, initLocalStorage } from '@23degrees/shared-code/content/content-utilities';
import { validatedataItSelf } from '@23degrees/shared-code/content/creator';
import { DataSetFields } from '@23degrees/shared-code/domain/content/entities/valueobjects/Fields';
import { ContentService as DomainContentService } from '@23degrees/shared-code/domain/content/services/content.service';
import { Content } from '@23degrees/shared-code/models';
import { cloneObject, sleep } from '@23degrees/shared-code/utilities';
import { MessageBus } from '@23degrees/shared/bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';
import { ContentService } from '@23degrees/shared/services/content/content.service';
import { FontService } from '@23degrees/shared/services/font';
import { FavoritesStoreService } from '@23degrees/stores/favorites/favorites.service';
import { compressMapData, decompressMapData } from '@23degrees/utilities/compress.utils';
import { isContentInjected } from '@23degrees/utilities/content.utils';

import { LOCALSTORAGEFIELDNAME, MESSAGE_RECIPIENTS, REQUEST_MESSAGES, RESPONSE_MESSAGES, SPINNER_OVERLAY_MESSAGES } from '@config';

import { DataManager } from '../data/data.manager';
import { filterVisual } from '../shared/visual.joins';

/*
    A global Content Manager
    - supports getting content by slug and emitting cached Base ContentItems

    ===== WARNING:
          TWO CONTENT ITEMS WITH THE SAME SLUG WILL HAVE THE SAME REFERENCE

    TODO - away all server calls!
          - just use this as client side cache and maybe for tracking changes
*/

interface ContentMap {
  [key: string]: Content;
}

@Injectable({
  providedIn: 'root',
})
export class ContentManager extends BaseClass {
  // holds our content identifer id and then timestamp
  // when content was loaded
  private contentItems: ContentMap = {};
  private contentHistory: any = {};

  private contentService: ContentService = inject(ContentService);
  private messageBus: MessageBus = inject(MessageBus);

  private favoritesStoreService: FavoritesStoreService = inject(FavoritesStoreService);
  private dataManager: DataManager = inject(DataManager);
  // private loadingService: KitLoadingService,
  private router: Router = inject(Router);
  private fontService: FontService = inject(FontService);
  private ngZone: NgZone = inject(NgZone);
  private notificationService: NotificationsService = inject(NotificationsService);

  constructor() {
    super();

    // console.log('[ContentManager] Constructor - isServer: ',this.isServer);
    this.messageBus.process(REQUEST_MESSAGES.ContentBySlug, this.processContentBySlug, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.InjectAutoCreatedVisual, this.processInjectAutoCreatedVisual, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.ContentUpdate, this.processContentUpdate, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.ContentPurge, this.processContentPurge, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.BotExecuteFunction, this.processBotExecuteFunction, this.componentDestroyed$);
    // creates a content object on the server either from injected source or as clone of existing object (save as new)
    this.messageBus.process(REQUEST_MESSAGES.ServerContentDelete, this.processServerContentDelete, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.ServerContentCreate, this.processServerContentCreate, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.ServerContentCreateFromExisting, this.processServerContentCreateFromExisting, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.ServerContentUpdate, this.processServerContentUpdate, this.componentDestroyed$);
    this.restoreSlugStateInSession();
    // console.log("[ContentManager] - ", ParentTypes);
    this.globalService.setContentStore(this.contentItems);
  }

  public getContent = (slug: string): Content => {
    // console.log('[ContentManager] getContent - managerId', this.managerId);
    return this.contentItems[slug];
  };

  private setContent = (slug: string, content: Content, updateReference: boolean = false) => {
    if (!this.isServer()) {
      console.log('[ContentManager] setContent', slug, { content });
    }

    this.contentItems[slug] = content;

    // CHECK: not really used, just blowing stack ???
    // this.contentStoreService.addContent(cloneObject(content));
  };

  //gregors domain - doesnt do anything at the moment
  private addHistory = (slug: string, prev: any, post: any) => {
    if (!this.contentHistory[slug]) this.contentHistory[slug] = [];
    const history = {
      date: new Date(),
      prev: prev,
      post: post,
    };

    this.contentHistory[slug].unshift(history);
    this.contentHistory[slug].length = Math.min(this.contentHistory[slug].length, 10); // MAX_HISTORY = 10
  };

  //gregors domain - doesnt do anything at the moment
  public getHistory = (slug: string) => {
    return this.contentHistory[slug];
  };

  /**
   * emptys complete cache (carefull should only be used on logout
   */
  public emptyCache = () => {
    this.contentItems = {};
    this.contentHistory = {};

    this.globalService.setContentStore(this.contentItems);

    try {
      sessionStorage.removeItem('sessionRestoreCheckoutContent');
    } catch (error) {
      //
    }
  };

  /**
   * - beim speichern von atlas und report müssen alle kinder mitgegeben werden
   * - früher konnte man direkt nach dem aufmachen des atlas ein save as machen
   *  -> alle Kinder müssen geladen sein -> jetzt kann eigentlich gecloned werden
   *  -> insofern könnte das weg
   *
   * -> beim update aber brauchen wir weiterhin die kinder
   * -> wenn das refaktoriert ist kann man das hier raushauen
   */
  public getChildrenBySlug = (slug: string) => {
    const parentContent = this.getContent(slug);
    const childSlugs = getContentChildren(parentContent);
    const returnChildren: Content[] = childSlugs.map((childSlug) => this.getContent(childSlug));
    return returnChildren;
  };

  public getContentByDataId = (dataId: string) => {
    if (!dataId) return;

    const contentArray = Object.values(this.contentItems);
    const content = contentArray.find((content) => content.typeSpecific?.dataId === dataId);

    return content;
  };

  /**
    Dirty hack to get already nice and laoded content item in the cache with new slug for tooltip injection
   */
  public injectAlreadyCachedContentDirectly = (content) => {
    if (!content.localStorage) {
      initLocalStorage(content);
    }

    this.setContent(content.slug, content);
  };

  /**
    when going to pay state
    saves state of current content and after redirect
    restores it
   */
  public saveSlugStateInSession = (slug) => {
    if (slug && this.contentItems[slug]) {
      // console.log('[ContentManager] - SaveSlugStateInSession found slug', slug);
      // easiest first its only a chart we just need to backup the chart!
      try {
        const content = this.contentItems[slug];
        const saveArray = [];
        saveArray.push(content);

        if (['atlas', 'report', 'dash'].includes(content.type)) {
          const children = this.getChildrenBySlug(content.slug);
          for (const child of children) {
            saveArray.push(child);
          }
        }
        // console.log('[ContentManager] - Saving item to session storage', slug, 'items: ', saveArray.length);
        sessionStorage.setItem('sessionRestoreCheckoutContent', JSON.stringify(saveArray));
      } catch (e) {
        // console.log('[ContentManager] - No access to session storage');
      }
    }
  };
  /**
   * one time restore!
   */
  public restoreSlugStateInSession = () => {
    if (!this.isServer()) {
      try {
        const items = sessionStorage.getItem('sessionRestoreCheckoutContent');
        if (items) {
          // console.log('[ContentManager] - trying to restore session state');
          const contentItems = JSON.parse(items);
          for (const content of contentItems) {
            // console.log('[Restoring]: ', content.slug);
            this.setContent(content.slug, content);
          }
          // only one time restore
          sessionStorage.removeItem('sessionRestoreCheckoutContent');
        }
      } catch (e) {
        // console.log('[ContentManager] - No access to session storage');
      }
    }
  };

  public processServerContentDelete = async (msg, recipients, referenceID) => {
    console.log('[DEPRECATED] - processServerContentDelete');
    const { contentItem, options } = msg;
    // delete on the server
    const res = await this.contentService.deleteContentPromise(contentItem._id);
    // delete from cache if it exists
    delete this.contentItems[contentItem.slug];
    // delete from favorites
    this.favoritesStoreService.remove(contentItem, true);

    // this.messageBus.emit(REQUEST_MESSAGES.ServerFavoriteDelete, recipients, { contentItem: contentItem }, referenceID);

    this.messageBus.emit(
      RESPONSE_MESSAGES.ServerContentDelete,
      recipients,
      {
        serverResponse: res,
        options: options,
      },
      referenceID,
    );
  };

  /**
   * creates a visual based on data and already cached content item
   * @param  msg        [description]
   * @param  recipients [description]
   * @return            [description]
   */
  private processInjectAutoCreatedVisual = async (msg, recipients, referenceID) => {
    console.log('[processInjectAutoCreatedVisual] msg: ', msg);
    const { slug, options } = msg;
    if (!slug) return;
    const startDate: any = new Date();
    const content = this.contentItems[slug];
    console.log('[processInjectAutoCreatedVisual] content - ', content);
    if (!content || content.type !== 'dataset') return;
    const data = (await this.dataManager.getDataById(content.typeSpecific.dataId)).data;
    console.log('[processInjectAutoCreatedVisual] data ', data);
    if (!data || data.length === 0) {
      this.ngZone.run(() => {
        this.messageBus.emit(
          RESPONSE_MESSAGES.InjectAutoCreatedVisual,
          recipients,
          {
            slug: content.slug,
            success: false,
          },
          referenceID,
        );
      });
    }
    console.log('[ContentManager] Inject Visual ', startDate);
    let found = false;
    creatorSupportedTypes.sort((a, b) => a.order - b.order);
    for (const typeObj of creatorSupportedTypes) {
      if (!found) {
        //TODO check Dimension would be enough here we dont need a complete visual Configuration
        //cause server just needs a type rest is done automatically
        const configurationResponse = DomainContentService.createVisualConfiguration(typeObj.type, content.typeSpecific.fields as DataSetFields, data, {
          requestId: Math.random().toString(),
          stack: [],
        });

        if (configurationResponse.isFailure) continue;
        const visualConfiguration = configurationResponse.getValue();

        if (visualConfiguration.type !== 'table') {
          found = true;
          const stub = await this.contentService.getStubByTypePromise(visualConfiguration.type, {
            options: {
              dataSetId: content._id,
              visualConfiguration: visualConfiguration,
            },
          });

          this.ngZone.run(() => {
            const endDate: any = new Date();
            const elapsedTime = endDate - startDate;
            // console.log('[ContentManager] finished injecting visual: ', elapsedTime);
            this.messageBus.emit(
              RESPONSE_MESSAGES.InjectAutoCreatedVisual,
              recipients,
              {
                slug: stub[visualConfiguration.type].slug,
                success: true,
              },
              referenceID,
            );
          });
        }
      }
    }

    if (!found) {
      this.ngZone.run(() => {
        this.messageBus.emit(
          RESPONSE_MESSAGES.InjectAutoCreatedVisual,
          recipients,
          {
            slug: content.slug,
            success: false,
          },
          referenceID,
        );
      });
    }
  };

  /**
   * Updating visuals with new data
   * @param msg
   * @param recipients
   * @param referenceID
   */
  private processBotExecuteFunction = async (msg, recipients, referenceID) => {
    // console.log('[ContentManager] - Executing Randy Function', msg);
    /*
    type: 'randyAction',
    intent: message.typeSpecific.intent,
    action: action,
    slug :   message.typeSpecific.slug
    */
    if (msg.intent === 'updateVisualWithNewDataSource') {
      // was the action the true value
      if (msg.action.value) {
        const slug = msg.slug;
        // console.log('[ContentManager] - Updating Content: ' + slug);
        const content = this.contentItems[msg.slug];
        // console.log('[ContentManager] - Content: ',content)
        // get the cached content
        // change derzeitigen data und metadataid
        // childUpdates
        if (content.typeSpecific.updatePossible) {
          if (ParentTypes.includes(content.type)) {
            let childUpdates: any;
            let notUpdatedChilds = [];

            if (msg.options?.specificChildUpate) {
              notUpdatedChilds = msg.options.specificChildUpate.filter((el) => !el.selected);
              childUpdates = msg.options.specificChildUpate.filter((el) => el.selected);
            } else {
              childUpdates = content.typeSpecific.updatePossible.childUpdates;
            }
            // console.log('[ContentManager] - Child Update ', content.slug);

            for (const childToUpdate of childUpdates) {
              // load child apply update locally
              if (!this.contentItems[childToUpdate.childSlug]) {
                await this.fetchContentBySlug(childToUpdate.childSlug);
              } else {
                await this.checkContentBySlug(this.contentItems[childToUpdate.childSlug], childToUpdate.childSlug);
              }

              const childContent = this.contentItems[childToUpdate.childSlug];

              applyDataSetUpdateToExistingContent(childContent, { client: true });

              if (childContent.localStorage?.referenceID) {
                this.messageBus.emit(
                  REQUEST_MESSAGES.RenderData,
                  [MESSAGE_RECIPIENTS.Chart, MESSAGE_RECIPIENTS.Map, MESSAGE_RECIPIENTS.Editor],
                  {
                    content: childContent,
                    dataId: childContent.typeSpecific.dataId,
                    options: {},
                  },
                  childContent.localStorage.referenceID,
                );
              }
            }
            // console.log('[ContentManager] - Child Update ', notUpdatedChilds);
            if (notUpdatedChilds.length > 0) {
              notUpdatedChilds = notUpdatedChilds.map((el) => {
                delete el.selected;
                return el;
              });
              content.typeSpecific.updatePossible.childUpdates = notUpdatedChilds;
            } else {
              delete content.typeSpecific.updatePossible;
            }

            content.localStorage.unsavedChanges = true;
            this.messageBus.emit(
              RESPONSE_MESSAGES.BotExecuteFunction,
              recipients,
              {
                slug: slug,
                options: msg.options,
              },
              referenceID,
            );
          } else {
            applyDataSetUpdateToExistingContent(content, { client: true });
            // console.log('[ContentManager] - Emiting Execute Function: ',slug, recipients,content);
            this.messageBus.emit(
              RESPONSE_MESSAGES.BotExecuteFunction,
              recipients,
              {
                slug: slug,
                options: msg.options,
              },
              referenceID,
            );
          }
        }
      }
    }
  };

  /**
   * TODO
   * deprecation needed
   * Today we do not let the user
   * change something and then save it
   * only: clone > update!
   * @param msg
   * @param recipients
   * @param referenceID
   * @returns
   */
  private processServerContentCreateFromExisting = async (msg, recipients, referenceID) => {
    const { slug, options } = msg;
    // TODO error handling
    let content = this.contentItems[slug];
    if (!slug || !content) {
      this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
      this.messageBus.emit(RESPONSE_MESSAGES.ServerContentCreateFromExisting, recipients, {}, referenceID);
      return;
    }

    if (options.folderSlug) {
      content = { ...content, folderSlug: options.folderSlug };
    }

    // inform global overlay what is happening;
    this.informSpinnerOverlay(`Creating <b>${content.fileName}</b> to <b>${options.collectionName}</b>`);
    console.log('[ContentManager] Create from exisiting- slug: ', slug, content.slug);
    // children conten types
    if (['atlas', 'report', 'dash'].includes(content.type)) {
      const requestObj: any = {};
      requestObj.content = content;
      requestObj.children = this.getChildrenBySlug(content.slug);
      try {
        const res = await this.contentService.createChildrenTypePromise(requestObj);
        const newContent = res.data;
        // if (options.collectionId) {
        //   const coll = await this.collectionService.addContentToCollectionPromise(options.collectionId, newContent._id);
        //   // console.log('[ContentManager] saved to collection res: ', coll);
        // }
        const reallyNewContent = await this.getContentBySlug(newContent.slug, false, false);
        initLocalStorage(reallyNewContent);
        this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
        this.messageBus.emit(
          RESPONSE_MESSAGES.ServerContentCreateFromExisting,
          recipients,
          {
            slug: reallyNewContent.slug,
          },
          referenceID,
        );
      } catch (error) {
        console.error(error);
        this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
        this.messageBus.emit(RESPONSE_MESSAGES.ServerContentCreateFromExisting, recipients, {}, referenceID);
      }
    } else if (content.type === 'dataset') {
      await this.ngZone.runOutsideAngular(async () => {
        try {
          const data = (await this.dataManager.getDataById(content.typeSpecific.dataId)).data;
          validatedataItSelf(content.typeSpecific.fields as DataSetFields, data);
          this.cleanLocalStorageFromData(data, LOCALSTORAGEFIELDNAME);
          content.created_at = new Date();
          content.versioning.source_id = uuid();
          const source = {
            data: data,
            dataSet: content,
            options: { dataPageSize: 2000, collectionId: options.collectionId },
          };
          const savedDataSet = await this.contentService.createDataSetVersionedPromise(source);
          const newContent = savedDataSet.data.dataSet;

          initLocalStorage(newContent);
          this.setContent(newContent.slug, newContent);
          // purge old dataId
          this.ngZone.run(() => {
            this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
            this.messageBus.emit(REQUEST_MESSAGES.DataPurge, [], { dataId: content.typeSpecific.dataId });
            // console.log('[CreateUploadEditor] AFTER UPDATE DATASET', savedDataSet);
            this.messageBus.emit(
              RESPONSE_MESSAGES.ServerContentCreateFromExisting,
              recipients,
              {
                slug: newContent.slug,
              },
              referenceID,
            );
          });
        } catch (error) {
          this.ngZone.run(() => {
            this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
            // console.log('[CreateUploadEditor] AFTER UPDATE DATASET', savedDataSet);
            this.messageBus.emit(RESPONSE_MESSAGES.ServerContentCreateFromExisting, recipients, {}, referenceID);
          });
        }
      });
    } else {
      try {
        const uploadContent = cloneObject(content);
        compressMapData(uploadContent);

        const res = await this.contentService.createFromExistingChildlessPromise(uploadContent);
        const newContent = res.data;
        // if (options.collectionId) {
        //   const coll = await this.collectionService.addContentToCollectionPromise(options.collectionId, newContent._id);
        // }
        const reallyNewContent = await this.getContentBySlug(newContent.slug, false, false);

        initLocalStorage(reallyNewContent);
        decompressMapData(reallyNewContent);

        this.setContent(reallyNewContent.slug, reallyNewContent);

        this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
        this.messageBus.emit(
          RESPONSE_MESSAGES.ServerContentCreateFromExisting,
          recipients,
          {
            slug: reallyNewContent.slug,
            content: reallyNewContent,
          },
          referenceID,
        );
      } catch (error) {
        this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
        this.messageBus.emit(RESPONSE_MESSAGES.ServerContentCreateFromExisting, recipients, {}, referenceID);
      }
    }
  };

  public cloneDataSetWithData = async (content: Content) => {
    try {
      const retData = await this.dataManager.getDataById(content.typeSpecific.dataId);
      const { data } = retData;

      // validate
      validatedataItSelf(content.typeSpecific.fields as DataSetFields, data);

      // clean
      this.cleanLocalStorageFromData(data, LOCALSTORAGEFIELDNAME);

      content.created_at = new Date();
      content.versioning.source_id = uuid();

      const context = {
        data: data,
        dataSet: content,
      };

      const retContent = await this.contentService.cloneDataSetVersionedPromise(context);
      const clonedContent = retContent.data.dataSet;

      initLocalStorage(clonedContent);
      this.setContent(clonedContent.slug, clonedContent);

      // purge old dataId
      this.dataManager.purgeData(content.typeSpecific.dataId);

      // inject new data
      this.dataManager.setDataById(clonedContent.typeSpecific.dataId, data);

      return clonedContent;
    } catch (error) {
      console.log('[cloneDataSetWithData]', error);
    }
  };

  public updateDataSetWithData = async (content: Content) => {
    try {
      const retData = await this.dataManager.getDataById(content.typeSpecific.dataId);
      const { data } = retData;

      // validate
      validatedataItSelf(content.typeSpecific.fields as DataSetFields, data);

      // clean
      this.cleanLocalStorageFromData(data, LOCALSTORAGEFIELDNAME);

      content.updated_at = new Date();
      // content.versioning.source_id = uuid();

      const context = {
        data: data,
        dataSet: content,
      };

      const retContent = await this.contentService.createDataSetVersionedPromise(context);
      const clonedContent = retContent.data.dataSet;

      initLocalStorage(clonedContent);
      this.setContent(clonedContent.slug, clonedContent);

      // purge old dataId
      this.dataManager.purgeData(content.typeSpecific.dataId);

      // inject new data
      this.dataManager.setDataById(clonedContent.typeSpecific.dataId, data);

      return clonedContent;
    } catch (error) {
      console.log('[updateDataSetWithData]', error);
    }
  };

  /**
   * TODO
   * deprecation needed
   * Today we do not let the user
   * change something and then save it
   * only: clone > update!
   * @param  msg        [description]
   * @param  recipients [description]
   * @return            [description]
   */
  private processServerContentCreate = async (msg, recipients, referenceID) => {
    const { slug, options } = msg;
    let content = this.contentItems[slug];
    console.log('[ContentManager] processServerContentCreate- slug: ', slug);
    if (!slug || !content) {
      this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
      this.messageBus.emit(RESPONSE_MESSAGES.ServerContentCreate, recipients, {}, referenceID);
      return;
    }

    if (options?.folderSlug) {
      content = { ...content, folderSlug: options.folderSlug };
    }

    // console.log('[ContentManager] - content to save: ', content);
    this.informSpinnerOverlay(`Saving ${content.fileName}</b> to <b>${options.collectionName}</b>`);
    /**
     *
     * Save Atlas and Report From Injected Item
     *
     */
    if (['atlas', 'report', 'dash'].includes(content.type)) {
      // we need to get the children and craft a special request object
      const requestObj: any = {};
      requestObj.content = content;
      requestObj.children = this.getChildrenBySlug(content.slug);
      try {
        const res = await this.contentService.createChildrenTypePromise(requestObj);
        const newContent = res.data;
        const reallyNewContent = await this.getContentBySlug(newContent.slug, false, false);
        initLocalStorage(reallyNewContent);
        this.messageBus.emit(RESPONSE_MESSAGES.ServerContentCreate, recipients, { slug: reallyNewContent.slug }, referenceID);
      } catch (error) {
        console.error('ERROR during ServerContentCreate ', error.message);
        this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
        this.messageBus.emit(RESPONSE_MESSAGES.ServerContentCreate, recipients, {}, referenceID);
        return;
      }
    } else if (content.type === 'dataset') {
      // got content and options
      // need data
      try {
        const data = (await this.dataManager.getDataById(content.typeSpecific.dataId)).data;
        await this.ngZone.runOutsideAngular(async () => {
          validatedataItSelf(content.typeSpecific.fields as DataSetFields, data);
          this.cleanLocalStorageFromData(data, LOCALSTORAGEFIELDNAME);
          const source = {
            data: data,
            dataSet: content,
            options: { dataPageSize: 2000, collectionId: options.collectionId },
          };
          const savedDataSet = await this.contentService.createDataSetVersionedPromise(source);
          const newContent = savedDataSet.data.dataSet;

          initLocalStorage(newContent);
          this.setContent(newContent.slug, newContent);
          console.log('[content manager] after content create new content: ', newContent.slug);
          this.ngZone.run(() => {
            //this.messageBus.emit(REQUEST_MESSAGES.DataPurge, [], { dataId: content.typeSpecific.dataId });
            //this.messageBus.emit(REQUEST_MESSAGES.ContentPurge, [], { slug: content.slug });
            this.messageBus.emit(
              RESPONSE_MESSAGES.ServerContentCreate,
              recipients,
              {
                slug: newContent.slug,
                dataset: true,
              },
              referenceID,
            );
          });
        });
      } catch (error) {
        this.ngZone.run(() => {
          console.error('ERROR during ServerContentCreate ', error.message);
          this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
          this.messageBus.emit(RESPONSE_MESSAGES.ServerContentCreate, recipients, {}, referenceID);
          return;
        });
      }
    } else {
      try {
        // just doublecheck to net let content be saved as child directly
        const uploadContent = cloneObject(content);
        if (uploadContent.typeSpecific?.isChild) {
          delete uploadContent.typeSpecific.isChild;
        }

        compressMapData(uploadContent);

        const res = await this.contentService.createContentPromise(uploadContent);
        const newContent = res.data;

        // if (options.collectionId) {
        //   const coll = await this.collectionService.addContentToCollectionPromise(options.collectionId, newContent._id);
        //   // console.log('[ContentManager] saved to collection res: ', coll);
        // }
        const reallyNewContent = await this.getContentBySlug(newContent.slug, false, false);
        initLocalStorage(reallyNewContent);
        decompressMapData(reallyNewContent);
        // TODO: // this.setContent(reallyNewContent.slug, reallyNewContent);
        this.messageBus.emit(RESPONSE_MESSAGES.ServerContentCreate, recipients, {
          slug: reallyNewContent.slug,
        });
      } catch (error) {
        console.error('ERROR during ServerContentCreate ', error.message);
        this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
        this.messageBus.emit(RESPONSE_MESSAGES.ServerContentCreate, recipients, {}, referenceID);
        return;
      }
    }
    this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
  };

  /**
   * TODO
   * deprecation needed
   * We need better childhandling for
   * report and atlas
   * @param msg
   * @param recipients
   * @param referenceID
   * @returns
   */
  private processServerContentUpdate = async (msg, recipients, referenceID) => {
    const { slug, options } = msg;

    const content = this.contentItems[slug];
    if (!slug || !content) {
      this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
      this.messageBus.emit(RESPONSE_MESSAGES.ServerContentUpdate, recipients, {}, referenceID);
      return;
    }
    // inform global overlay what is happening;
    this.informSpinnerOverlay(`Updating <b>${content.fileName}</b>`);
    // console.log('[ContentManager] managerId: ', this.managerId, '- content to update: ', content, recipients);
    // children conten types
    if (['atlas', 'report', 'dash'].includes(content.type)) {
      // get current children from cache
      const children = this.getChildrenBySlug(content.slug);
      const requestObj: any = {};
      requestObj.content = content;
      requestObj.children = children;
      // console.log('[ContentManager] atlas content update atlas',requestObj);
      try {
        const res = await this.contentService.updateContentWithChilrenPromise(requestObj);
        const newContent = res.data.content;
        const newChildren = res.data.children;
        // if (options.collectionId) {
        //   const coll = await this.collectionService.addContentToCollectionPromise(options.collectionId, newContent._id);
        // }
        // update in Cache
        const reallyNewContent = await this.getContentBySlug(newContent.slug, false, false);
        initLocalStorage(reallyNewContent);
        //IMPORTANT!
        // console.log('[processServerContentUpdate] atlas/report set refId', referenceID);
        reallyNewContent.localStorage.referenceID = referenceID;
        // TODO: //  this.setContent(reallyNewContent.slug, reallyNewContent);
        for (const child of newChildren) {
          initLocalStorage(child);
          this.setContent(child.slug, child);
        }
        this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
        this.messageBus.emit(
          RESPONSE_MESSAGES.ServerContentUpdate,
          recipients,
          {
            slug: reallyNewContent.slug,
            content: reallyNewContent,
          },
          referenceID,
        );
      } catch (error) {
        console.error(`ERROR while ServerContentUpdate for slug: ${slug} ${error.message}`);
        this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
        this.messageBus.emit(RESPONSE_MESSAGES.ServerContentUpdate, recipients, {}, referenceID);
        return;
      }
    } else if (content.type === 'dataset') {
      await this.ngZone.runOutsideAngular(async () => {
        const data = JSON.parse(JSON.stringify((await this.dataManager.getDataById(content.typeSpecific.dataId)).data));
        validatedataItSelf(content.typeSpecific.fields as DataSetFields, data);
        this.cleanLocalStorageFromData(data, LOCALSTORAGEFIELDNAME);
        const source = {
          data: data,
          dataSet: content,
          options: { dataPageSize: 3000, collectionId: options.collectionId },
        };
        try {
          const savedDataSet = await this.contentService.createDataSetVersionedPromise(source);
          const newContent = savedDataSet.data.dataSet;
          initLocalStorage(newContent);
          // console.log('[processServerContentUpdate] dataset set refId', referenceID);
          newContent.localStorage.referenceID = referenceID;
          this.setContent(newContent.slug, newContent);
          // purge old dataId
          this.ngZone.run(() => {
            this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
            this.messageBus.emit(REQUEST_MESSAGES.DataPurge, [], { dataId: content.typeSpecific.dataId });
            // console.log('[CreateUploadEditor] AFTER UPDATE DATASET', savedDataSet);
            this.messageBus.emit(
              RESPONSE_MESSAGES.ServerContentUpdate,
              recipients,
              {
                slug: newContent.slug,
                content: newContent,
              },
              referenceID,
            );
          });
        } catch (error) {
          this.ngZone.run(() => {
            console.error(error);
            this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
            this.messageBus.emit(RESPONSE_MESSAGES.ServerContentUpdate, recipients, {}, referenceID);
          });
        }
      });
    } else {
      try {
        const uploadContent = cloneObject(content);
        compressMapData(uploadContent);

        const res = await this.contentService.updateContentPromise(uploadContent);
        const newContent = res.data;
        this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
        // update in Cache

        const reallyNewContent = await this.getContentBySlug(newContent.slug, false, false);
        initLocalStorage(reallyNewContent);
        decompressMapData(reallyNewContent);

        // console.log('[processServerContentUpdate] visual set refId', referenceID);
        reallyNewContent.localStorage.referenceID = referenceID;
        // TODO: //this.setContent(reallyNewContent.slug, reallyNewContent);

        // console.log('[ContentManager] - After server Update inform ', recipients);
        this.messageBus.emit(
          RESPONSE_MESSAGES.ServerContentUpdate,
          recipients,
          {
            slug: reallyNewContent.slug,
            content: reallyNewContent,
          },
          referenceID,
        );
      } catch (error) {
        console.error(`ERROR while ServerContentUpdate for slug: ${slug} ${error.message}`);
        this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.CloseSpinnerOverlay, ['spinneroverlay'], {});
        this.messageBus.emit(RESPONSE_MESSAGES.ServerContentUpdate, recipients, {}, referenceID);
        return;
      }
    }
  };

  public processContentUpdate = async (msg, recipients, referenceID) => {
    if (!msg.slug) return;
    // console.log('[ContentManager] update:', msg.slug);
    const content = msg.content;
    // const { valid, content } = contentUtilities.validate(msg.content);
    // init localstorage if it was not set --> should only happen in the case
    // of injected content
    if (!content.localStorage) {
      // add local cache
      initLocalStorage(content);
      // referenceID needs to be set
      if (referenceID) {
        // console.log('[processContentUpdate] set refId', referenceID);
        content.localStorage.referenceID = referenceID;
      }
    }

    //loading fonts
    if (!this.isServer()) {
      await this.fontService.loadContentFonts(content);
    }

    // const prevState = objectDiff.diff(content, reference);
    // const postState = objectDiff.diff(reference, content);
    // this.addHistory(content.slug, prevState, postState);

    this.setContent(content.slug, content, true);

    this.messageBus.emit(
      RESPONSE_MESSAGES.ContentUpdate,
      recipients,
      {
        slug: content.slug,
        content: this.contentItems[content.slug],
        options: msg.options,
      },
      referenceID,
    );
  };

  /**
   * purges contentItems from the local cache
   * For ParentTypes it also purges the children
   * -- be aware if content structure changes for childtypes - these changes
   * have to be reflected here!
   * @param   msg
   * @param   recipients array string
   *
   */
  private processContentPurge = (msg, recipients, referenceID) => {
    // console.log('[ContentManager][processContentPurge]', { msg, recipients, referenceID });
    const { slug } = msg;
    if (!slug) return;
    this.contentPurge(slug, true);
    this.messageBus.emit(RESPONSE_MESSAGES.ContentPurge, recipients, { slug: msg.slug }, referenceID);
  };

  public contentPurge = (slug, purgeData = false) => {
    // check if childType
    if (this.contentItems[slug] && ParentTypes.includes(this.contentItems[slug].type)) {
      // purge childItems
      const parent = this.contentItems[slug];

      if (parent.type === 'atlas') {
        const childrenSlugs = parent.typeSpecific.atlas.children.map((el) => el.slug);
        for (const childSlug of childrenSlugs) {
          delete this.contentItems[childSlug];
        }
      }

      if (parent.type === 'dash') {
        const childrenSlugs = parent.typeSpecific.dash.children.map((el) => el.slug);
        for (const childSlug of childrenSlugs) {
          delete this.contentItems[childSlug];
        }
      }

      if (parent.type === 'report') {
        let childrenConf = [];
        for (const chapter of parent.typeSpecific.report.chapters) {
          childrenConf = childrenConf.concat(chapter.children);
        }
        const childrenSlugs = childrenConf.map((el) => el.slug);
        for (const childSlug of childrenSlugs) {
          delete this.contentItems[childSlug];
        }
      }
    }

    // purge that data
    if (purgeData) {
      if (this.contentItems[slug] && this.contentItems[slug].type === 'dataset') {
        const content = this.contentItems[slug];
        if (content?.typeSpecific?.dataId) {
          this.dataManager.purgeData(content.typeSpecific.dataId);
        }
      }
    }

    // delete content
    delete this.contentItems[slug];

    try {
      sessionStorage.removeItem('content');
    } catch (e) {
      //
    }
    console.log('[ContentManager] purged:', slug);
    return slug;
  };

  /**
   * gets content as awaitable by slug
   * @param  !this.contentItems[slug] [description]
   * @return                          [description]
   */
  public getContentBySlug = async (slug: string, isPublic: boolean = false, redirectOnNotFound: boolean = false) => {
    if (!slug) return;
    // console.log('[getContentBySlug] slug:', slug, ', cache:', this.contentItems[slug] ? true : false, ', wizard:', wizardContext);
    // fetch / update content
    if (!this.contentItems[slug]) {
      await this.fetchContentBySlug(slug, isPublic, redirectOnNotFound);
    }

    return this.contentItems[slug];
  };

  private processContentBySlug = async (msg, recipients, referenceID) => {
    const { slug } = msg;
    if (!slug) return;

    // fetch / update content
    if (!this.contentItems[slug]) {
      await this.fetchContentBySlug(slug, msg.public);
    } else {
      if (msg.checkForUpdate && !this.contentItems[slug].injected) {
        await this.checkContentBySlug(this.contentItems[slug], slug, msg.public);
      }
    }

    // special query for joining visuals
    if (msg.filterVisual?.fieldName && this.contentItems[slug]) {
      if (this.contentItems[slug].typeSpecific?.fields[msg.filterVisual.fieldName]) {
        const filterField = this.contentItems[slug].typeSpecific.fields[msg.filterVisual.fieldName];
        filterVisual(this.contentItems[slug], filterField, msg.filterVisual.filterValue);
      }
    }

    if (msg.branchFilter) {
      const fieldNames = Object.keys(msg.branchFilter);
      const hasFields = this.contentItems[slug].typeSpecific?.fields && fieldNames.every((key) => this.contentItems[slug].typeSpecific.fields[key]);
      if (hasFields) {
        for (const fieldName of fieldNames) {
          this.contentItems[slug].typeSpecific.fields[fieldName].selectedValues = [msg.branchFilter[fieldName]];
          // filterVisual(this.contentItems[slug], { fieldName: fieldName }, msg.branchFilter[fieldName]);
        }
      }
    }

    this.emitCachedContent(msg, recipients, referenceID);
  };

  private emitCachedContent = (msg, recipients, referenceID) => {
    if (!this.getContent(msg.slug)) return;
    // console.log('[ContentManager] emit cached:', msg.slug);

    if (referenceID) {
      // console.log('[emitCachedContent] set refId', referenceID, this.contentItems[msg.slug].localStorage.referenceID);
      // if (!this.contentItems[msg.slug].localStorage.referenceID) {
      this.contentItems[msg.slug].localStorage.referenceID = referenceID;
      // }
    }

    this.messageBus.emit(
      RESPONSE_MESSAGES.ContentBySlug,
      recipients,
      {
        slug: msg.slug,
        content: this.contentItems[msg.slug],
        options: msg.options,
      },
      referenceID,
    );
  };

  private checkContentBySlug = async (oldContent, slug, isPublic = false) => {
    // console.log('checking Slug: ', slug);
    if (!slug || slug.includes('~')) {
      //some content clone or injected shit we wont check this
      return;
    }

    let res: Content;
    if (isPublic) {
      // console.log('[ContentManager] - PUBLIC ', slug);
      try {
        res = await this.contentService.getPublicContentBySlugEmbed(slug);
      } catch (e) {
        // console.log('[ContentManager] - Error From Server while Checking slug: ', slug, e);
      }
    } else {
      // console.log('[ContentManager] Check Content - PRIVATE ', slug);
      try {
        res = await this.contentService.getContentBySlugPromise(slug);
      } catch (e) {
        // console.log('[ContentManager] - Error From Server while Checking slug: ', slug, e);
      }
    }

    if (!res) {
      // console.log('[ContentManager] Check content by Slug not found:', slug);
      // this.loadingService.global.end('content');
      // this.loadingService.global.end('routing');
      // fix this once user levles come
      this.notificationService.error('404: Content not found!');
      this.router.navigate(['/']);
      return;
    }
    // console.log('[ContentManager] - Checking : ', oldContent._id, res._id, oldContent.updated_at, res.updated_at);
    if (oldContent._id !== res._id || oldContent.updated_at !== res.updated_at) {
      const valid = true;
      const content = res;
      // const { valid, content } = contentUtilities.validate(res);
      // console.log('[ContentManager] - Found Updated Content', content.slug);
      if (valid) {
        initLocalStorage(content);
        decompressMapData(content);
        this.setContent(content.slug, content);

        if (!this.isServer()) {
          await this.fontService.loadContentFonts(content);
        }

        const limit = pLimit(2);
        const childSlugs = getContentChildren(content);
        const childFetches = childSlugs.map((childSlug) => limit(() => this.fetchContentBySlug(childSlug, isPublic)));

        Promise.all(childFetches).then(() => {
          console.log(`[ContentManager] ${childSlugs.length} children loaded`);
        });
      }
    }

    return;
  };

  private fetchContentBySlug = async (slug, isPublic = false, redirectOnNotFound = true) => {
    if (!slug) return;
    // console.log('[ContentManager] - Fetch content by Slug', slug, 'isPublic:', isPublic, 'redirect:', redirectOnNotFound);
    // wizard attributes

    if (slug.includes('~')) {
      slug = slug.substring(0, slug.indexOf('~'));
    }

    let res: Content;

    if (!this.isServer()) {
      /**
       * PREVENT 404 on reload
       */
      try {
        const msg = sessionStorage.getItem('content');
        if (msg) {
          // console.log('SESSSION MSG', msg);
          const storageItem = JSON.parse(msg);
          // console.log('[ContentManager] got content from storage', storageItem);
          if (slug === storageItem.slug) {
            // console.log('[ContentManager] slug is ok',  storageItem.slug);
            // console.log('[ContentManager] content from storage',  storageItem);
            if (isContentInjected(storageItem)) {
              res = storageItem;
            }
          } else {
            // console.log('[ContentManager] slug is not ok, removing form storage',  storageItem.slug);
            sessionStorage.removeItem('content');
          }
        }
      } catch (e) {
        // console.log('[ContentManager] - No Accces to session Storage', e);
      }
    }

    // if not injectedContent
    if (!res) {
      if (isPublic) {
        try {
          res = await this.contentService.getPublicContentBySlugEmbed(slug);
        } catch (e) {
          if (e?.error?.status && e.error.action === 'getBySlugPublic') {
            // console.log('[ContentManager] - Valid Error from server:', e.error);
          } else {
            // console.log('[ContentManager] - Unknown error while loading content', slug, e);
            // trying to load again after a quick sleep
            await sleep(500);
            // console.log('[ContentManager] - after sleep get content');
            try {
              res = await this.contentService.getPublicContentBySlugEmbed(slug);
            } catch {
              // console.log('[ContentManager] - error while loading content on second load', slug, e);
            }
          }
        }
        // console.log('[ContentManager] - PUBLIC ', slug);
      } else {
        // console.log('[ContentManager] - PRIVATE ', slug);
        try {
          res = await this.contentService.getContentBySlugPromise(slug);
        } catch (e) {
          // console.log('[ContentManager] - Error From Server while loading slug:', slug, e);
        }
        // load children for childcontenttypes
        if (res && ['atlas', 'report', 'dash'].includes(res.type)) {
          if (!this.isServer()) {
            // console.log('[ContentManager] - Loading children');
            const content = res;
            // const content = contentUtilities.validate(res).content;
            await this.fontService.loadContentFonts(content);

            initLocalStorage(content);
            this.setContent(content.slug, content);

            const limit = pLimit(2);
            const childSlugs = getContentChildren(content);
            const childFetches = childSlugs.map((childSlug) => limit(() => this.fetchContentBySlug(childSlug, isPublic)));

            Promise.all(childFetches).then(() => {
              console.log(`[ContentManager] ${childSlugs.length} children loaded`);
            });
          }
        }
      }
    }

    if (!res) {
      if (!this.isServer()) {
        /**
         * PREVENT 404 on reload
         */
        try {
          const msg = sessionStorage.getItem('content');
          if (msg) {
            const storageItem = JSON.parse(msg);
            // console.log('[ContentManager] got content from storage', storageItem);
            if (slug === storageItem.slug) {
              // console.log('[ContentManager] slug is ok',  storageItem.slug);
              // console.log('[ContentManager] content from storage',  storageItem);
              res = storageItem;
            } else {
              // console.log('[ContentManager] slug is not ok, removing form storage',  storageItem.slug);
              sessionStorage.removeItem('content');
            }
          }
        } catch (e) {
          // console.log('[ContentManager] - No Accces to session Storage', e);
        }
      }

      if (!res) {
        // console.log('[ContentManager] not found:', slug, 'do redirect', redirectOnNotFound);
        // this.loadingService.global.end('content');
        // this.loadingService.global.end('routing');
        if (redirectOnNotFound) {
          // fix this once user levles come
          if (isPublic) {
            this.router.navigate(['/404'], { replaceUrl: true });
          } else {
            this.notificationService.error('404: Content not found!');
            this.router.navigate(['/'], { replaceUrl: true });
          }
        }
        return;
      }
    }

    const valid = true;
    const content: Content = res;
    // const { valid, content } = contentUtilities.validate(res);
    // console.log('[ContentManager] - Init Local Storage', content.slug);

    if (valid) {
      initLocalStorage(content);
      decompressMapData(content);
      this.setContent(content.slug, content);

      if (!this.isServer()) {
        await this.fontService.loadContentFonts(content);
      }

      // inlineData && isContentInjected
      if (content.typeSpecific.inlineData && isContentInjected(content)) {
        const hashId = uuid();
        const baseDataId = content.typeSpecific.dataId.split('~')[0];
        const dataId = `${baseDataId}~${hashId}`;

        const dataObj = await this.dataManager.getDataById(baseDataId);

        this.dataManager.setDataById(dataId, dataObj.data);
        content.typeSpecific.dataId = dataId;
        // console.log('>>> PATCHING dataId', baseDataId, dataId);
      }
    } else {
      // TODO error handling
      // console.log('[ContentManager] validation failed:', res.slug);
    }
    return;
  };

  // inplace LocalStorage FieldName removal
  private cleanLocalStorageFromData = (data, localStorageFieldName) => {
    for (const row of data) {
      delete row[localStorageFieldName];
    }
    return;
  };

  private informSpinnerOverlay = (
    loadinInfo: string,
    success: boolean = false,
    preventOverlayClosing: boolean = true,
    overlayOpen: boolean = true,
    showEmbed: boolean = false,
    content: any = null,
  ) => {
    this.messageBus.emit(SPINNER_OVERLAY_MESSAGES.ShowSpinnerOverlay, ['spinneroverlay'], {
      type: 'default',
      default: {
        content: content,
        preventOverlayClosing: preventOverlayClosing,
        showEmbed: showEmbed,
        loadingInfo: loadinInfo,
      },
    });
  };

  public setSessionStorage(content) {
    try {
      // console.log('[ContentManager] - Saving item to session storage', content.slug);
      sessionStorage.setItem('content', JSON.stringify(content));
    } catch (e) {
      // console.log('[ContentManager] - No access to session storage');
    }
  }
}
